import { CognitoJwtVerifier } from 'aws-jwt-verify';
import { type CognitoAccessTokenPayload } from 'aws-jwt-verify/jwt-model';
import { getCognitoOAuthConfig } from './cognito-auth-config.js';

type OAuthTokenResponse = {
  id_token: string;
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: 'Bearer';
};

type IdToken = {
  'custom:displayname': string;
  'cognito:username': string;
};

const createJwtVerifier = (poolId: string, clientId: string) =>
  CognitoJwtVerifier.create({
    userPoolId: poolId,
    clientId: clientId,
    tokenUse: 'access'
  });

export class OAuthSession {
  private static jwtVerifier: ReturnType<typeof createJwtVerifier>;

  public static async init(code: string | null): Promise<boolean> {
    const { authorizationServerUrl, clientId, poolId } = getCognitoOAuthConfig();
    OAuthSession.jwtVerifier = createJwtVerifier(poolId, clientId);

    if (!code) {
      return true;
    }

    const params = {
      grant_type: 'authorization_code',
      client_id: clientId,
      code: code,
      redirect_uri: this.getLoginRedirectUri()
    };

    const response = await fetch(`${authorizationServerUrl}/oauth2/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: new URLSearchParams(Object.entries(params)).toString()
    });

    if (response.ok) {
      const tokens = (await response.json()) as OAuthTokenResponse;
      OAuthSession.writeOAuthTokenResponseToSession(tokens);
      return true;
    }

    return false;
  }

  public static getAccessToken(): string | undefined {
    const accessToken = window.sessionStorage.getItem('OAuthSession.accessToken');

    if (!accessToken) {
      return undefined;
    }

    return accessToken;
  }

  private static getIdToken(): string | undefined {
    const idToken = window.sessionStorage.getItem('OAuthSession.idToken');

    if (!idToken) {
      return undefined;
    }

    return idToken;
  }

  public static getRefreshToken(): string | undefined {
    const refreshToken = window.sessionStorage.getItem('OAuthSession.refreshToken');

    if (!refreshToken) {
      return undefined;
    }

    return refreshToken;
  }

  public static getLoginUrl(): string {
    const { clientId, authorizationServerUrl } = getCognitoOAuthConfig();
    return `${authorizationServerUrl}/login?client_id=${clientId}&response_type=code&scope=aws.cognito.signin.user.admin+email+openid+phone+profile&redirect_uri=${this.getLoginRedirectUri()}`;
  }

  public static getLogoutUrl(): string {
    const { clientId, authorizationServerUrl } = getCognitoOAuthConfig();
    return `${authorizationServerUrl}/logout?client_id=${clientId}&logout_uri=${this.getLogoutRedirectUri()}`;
  }

  public static getUserName(): string | undefined {
    const idToken = OAuthSession.getIdToken();

    if (!idToken) {
      return;
    }

    const parsedIdToken = OAuthSession.decodeIdToken(idToken);

    return parsedIdToken['custom:displayname'] ?? parsedIdToken['cognito:username'];
  }

  private static readonly getLoginRedirectUri = () => `${window.location.origin}/login`;

  private static readonly getLogoutRedirectUri = () => `${window.location.origin}/logout`;

  static saveLocationBeforeLogin(location: string) {
    window.sessionStorage.setItem('OAuthSession.locationBeforeLogin', location);
  }

  static restoreLocationBeforeLogin() {
    const location = window.sessionStorage.getItem('OAuthSession.locationBeforeLogin') || '/';
    window.sessionStorage.removeItem('OAuthSession.locationBeforeLogin');
    window.location.href = location;
  }

  private static async verifyJwtToken(token: string): Promise<false | CognitoAccessTokenPayload> {
    try {
      return await OAuthSession.jwtVerifier.verify(token);
    } catch {
      return false;
    }
  }

  private static async verifyAccessToken(): Promise<false | CognitoAccessTokenPayload> {
    try {
      const accessToken = OAuthSession.getAccessToken();
      if (!accessToken) {
        return false;
      }

      const verifyedTokenPayload = await OAuthSession.verifyJwtToken(accessToken);
      if (verifyedTokenPayload) {
        return verifyedTokenPayload;
      }

      const refreshToken = OAuthSession.getRefreshToken();
      if (!refreshToken) {
        return false;
      }

      const refreshedAccessToken = await OAuthSession.refreshAccessToken(refreshToken);
      if (!refreshedAccessToken) {
        return false;
      }

      return await OAuthSession.verifyJwtToken(refreshedAccessToken);
    } catch {
      return false;
    }
  }

  public static async getScopes(): Promise<string[]> {
    const payload = await OAuthSession.verifyAccessToken();
    return payload ? payload.scope.split(' ') : [];
  }

  public static async isActive(): Promise<boolean> {
    return Boolean(await OAuthSession.verifyAccessToken());
  }

  private static async refreshAccessToken(refreshToken: string): Promise<false | string> {
    const { clientId, authorizationServerUrl } = getCognitoOAuthConfig();
    const params = {
      grant_type: 'refresh_token',
      client_id: clientId,
      refresh_token: refreshToken
    };

    const response = await fetch(`${authorizationServerUrl}/oauth2/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: new URLSearchParams(Object.entries(params)).toString()
    });

    if (response.ok) {
      const tokens = (await response.json()) as OAuthTokenResponse;
      OAuthSession.writeOAuthTokenResponseToSession({ ...tokens, refresh_token: refreshToken });
      return tokens.access_token;
    }

    return false;
  }

  private static writeOAuthTokenResponseToSession(response: OAuthTokenResponse): void {
    window.sessionStorage.setItem('OAuthSession.accessToken', response.access_token);
    window.sessionStorage.setItem('OAuthSession.refreshToken', response.refresh_token);
    window.sessionStorage.setItem('OAuthSession.idToken', response.id_token);
  }

  public static removeOAuthTokenResponseFromSession(): void {
    window.sessionStorage.removeItem('OAuthSession.accessToken');
    window.sessionStorage.removeItem('OAuthSession.refreshToken');
    window.sessionStorage.removeItem('OAuthSession.idToken');
  }

  private static decodeIdToken(token: string): IdToken {
    const base64Url = token.split('.')[1];
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64Url)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload) as IdToken;
  }
}
